import { useMemo } from "react";
import AdminLayout from "../../layout/AdminLayoutSewa";

function money(v) {
  return Number(v || 0).toFixed(2);
}

export default function ReportTablePage({
  title = "",
  subtitle = "",
  loading = false,
  template = "perumahan",
  setTemplate = () => {},
  dateMode = "date",
  dateValue = "",
  setDateValue = () => {},
  search = "",
  setSearch = () => {},
  summary = {},
  kawasanSummary = [],
  items = [],
  onRefresh = () => {},
  itemColumns = [],
}) {
  const safeKawasanSummary = Array.isArray(kawasanSummary) ? kawasanSummary : [];
  const safeItems = Array.isArray(items) ? items : [];
  const safeItemColumns = Array.isArray(itemColumns) ? itemColumns : [];

  const filteredKawasan = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return safeKawasanSummary;

    return safeKawasanSummary.filter((r) =>
      [r?.kawasan, r?.kod_kawasan]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q))
    );
  }, [safeKawasanSummary, search]);

  return (
    <AdminLayout>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          </div>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-600">Template</label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="border rounded-md w-full p-2 text-sm bg-white"
            >
              <option value="perumahan">Perumahan</option>
              <option value="penjaja">Penjaja</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">
              {dateMode === "month" ? "Bulan" : "Tarikh Rujukan"}
            </label>
            <input
              type={dateMode === "month" ? "month" : "date"}
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              className="border rounded-md w-full p-2 text-sm bg-white"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Search Kawasan</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kawasan / Kod Kawasan"
              className="border rounded-md w-full p-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-4 rounded border bg-blue-50">
            <div className="text-xs text-gray-600">PERIOD</div>
            <div className="text-lg font-bold text-blue-700 mt-1">{summary?.period || "-"}</div>
          </div>

          <div className="p-4 rounded border bg-indigo-50">
            <div className="text-xs text-gray-600">JUMLAH REKOD</div>
            <div className="text-2xl font-bold text-indigo-700 mt-1">{summary?.total_count || 0}</div>
          </div>

          <div className="p-4 rounded border bg-green-50">
            <div className="text-xs text-gray-600">JUMLAH (RM)</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{money(summary?.total_amount)}</div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 border">Kawasan</th>
                <th className="text-left p-2 border">Kod</th>
                <th className="text-left p-2 border">Jumlah Rekod</th>
                <th className="text-left p-2 border">Jumlah (RM)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-3 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredKawasan.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-3 text-center text-gray-500">
                    Tiada data.
                  </td>
                </tr>
              ) : (
                filteredKawasan.map((r, idx) => (
                  <tr key={`${r?.kawasan || "kawasan"}-${idx}`} className="border-t hover:bg-gray-50">
                    <td className="p-2 border font-semibold">{r?.kawasan || "-"}</td>
                    <td className="p-2 border">{r?.kod_kawasan || "-"}</td>
                    <td className="p-2 border">{r?.total_count || 0}</td>
                    <td className="p-2 border">{money(r?.total_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-5">
          <div className="text-sm font-semibold text-gray-700 mb-2">Rekod Terperinci</div>
          <div className="max-h-80 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {safeItemColumns.length === 0 ? (
                    <th className="text-left p-2 border">Data</th>
                  ) : (
                    safeItemColumns.map((col) => (
                      <th key={col.key} className="text-left p-2 border">
                        {col.label}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {safeItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={safeItemColumns.length || 1}
                      className="p-3 text-center text-gray-500"
                    >
                      Tiada rekod.
                    </td>
                  </tr>
                ) : safeItemColumns.length === 0 ? (
                  safeItems.map((row, idx) => (
                    <tr key={row?.id || idx} className="border-t">
                      <td className="p-2 border">{JSON.stringify(row)}</td>
                    </tr>
                  ))
                ) : (
                  safeItems.map((row, idx) => (
                    <tr key={row?.id || idx} className="border-t">
                      {safeItemColumns.map((col) => (
                        <td key={col.key} className="p-2 border">
                          {col.render ? col.render(row?.[col.key], row) : row?.[col.key] || "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}